/**
 * Aave V4 user state builder — single-spoke version.
 *
 * After the multi-market refactor (where each Aave V4 spoke is its own
 * lender key), this builder operates on **one isolated spoke** at a time:
 *
 * - `payload.lendingPositions` only contains positions in this spoke
 *   (the parser groups by spoke before invoking the builder).
 * - `lenderData` is the global meta map and may contain entries for
 *   many spokes — we filter to the ones that share this spoke's lender
 *   key, identified by the prefix on any position's marketUid.
 * - The risk premium is computed once per builder invocation from this
 *   spoke's collateral mix only — no cross-spoke grouping, no strict-vs-
 *   lenient config-resolver distinction.
 * - Health factor, borrow capacity, withdrawable/borrowable, APRs are
 *   all per-spoke isolated quantities. A user can be liquidatable in
 *   one spoke and safe in another; that's surfaced as two separate
 *   `UserData` instances at the dispatcher level (each one a single-
 *   account `data: [entry]` shape, mirroring V3).
 */

import { divideAccrualsToAprs } from '../utils'
import { safeDivide } from '../utils/formatting'
import { getOraclePrice } from '../utils/oraclePrice'
import {
  buildUnderlyingInfo,
  getMarketUidsFromMeta,
  LenderCrossPoolMeta,
  LenderYieldComplete,
  UserData,
} from '../utils/types'

/**
 * Resolve the user's pinned dynamic config entry for a reserve.
 *
 * After the multi-market refactor, config keys are plain `dynamicConfigKey`
 * strings (no spoke prefix — the spoke is already encoded in the lender key).
 * Returns `undefined` if no resolution is possible; the caller skips the
 * position rather than silently using a different config.
 */
function resolveDynamicConfig(
  configs: Record<string, any> | null | undefined,
  userConfigKey: string | undefined,
): any | undefined {
  if (!configs) return undefined
  if (userConfigKey && configs[userConfigKey]) return configs[userConfigKey]
  // Fallback to the first config entry. Unlike the pre-refactor lenient
  // resolver, this can no longer pull from a different spoke — every
  // entry in `configs` belongs to the same spoke as the position.
  const entries = Object.values(configs)
  return entries.length > 0 ? entries[0] : undefined
}

/**
 * Get oracle price for a reserve. After the refactor each meta entry is
 * already scoped to one spoke, so the global `getOraclePrice` is correct
 * — no per-spoke disambiguation needed.
 */
function getSpokeScopedPrice(meta: LenderYieldComplete): number {
  return getOraclePrice(meta)
}

export function createAaveV4UserState(
  payload: any,
  lenderData: LenderCrossPoolMeta,
  totalDeposits24h: number = 0,
  totalDebt24h: number = 0,
): UserData {
  const { chainId, account } = payload
  const positions = payload.lendingPositions ?? {}
  const positionMarketUids = Object.keys(positions)

  // Determine the per-spoke lender key from the first position. All
  // positions in `payload.lendingPositions` share the same lender key
  // because the parser groups by spoke before invoking the state builder.
  const spokeLenderKey =
    positionMarketUids.length > 0 ? positionMarketUids[0].split(':')[0] : ''

  // Filter the global meta map to marketUids that belong to this spoke.
  // Used by pass 2 to fill in borrow-only stubs for assets the user
  // hasn't touched yet but could potentially borrow.
  const allMarketUids = getMarketUidsFromMeta(lenderData)
  const spokeMarketUids = spokeLenderKey
    ? allMarketUids.filter((uid) => uid.startsWith(spokeLenderKey + ':'))
    : []

  // ── Off-chain risk premium (single-spoke version) ──────────────────
  //
  // Reproduces Spoke._calculateUserAccountData(user) for THIS spoke. No
  // grouping, no cross-spoke awareness — every position passed in is
  // already in the same spoke. The collateralRiskBps comes from the
  // meta entry directly (it's stamped on the per-spoke entry by the
  // public-data normalizer).
  type PremiumCollateral = { riskBps: number; valueUsd: number }
  const collaterals: PremiumCollateral[] = []
  let totalDebtForPremium = 0

  for (const marketUid of positionMarketUids) {
    const meta = lenderData?.[marketUid]
    const pos = positions[marketUid]
    if (!meta || !pos) continue

    const userConfigKey = (pos as any).userConfigKey as string | undefined
    const userConfig = resolveDynamicConfig(
      (meta as any).configs ?? null,
      userConfigKey,
    )
    const collateralFactor = userConfig?.collateralFactor ?? 0

    const depositsUSDOracle =
      (pos as any).depositsUSDOracle ?? pos.depositsUSD ?? 0
    const debtUSDOracle = (pos as any).debtUSDOracle ?? pos.debtUSD ?? 0

    // Collateral side — same gating as Spoke._calculateUserAccountData
    if (
      pos.collateralEnabled &&
      !userConfig?.collateralDisabled &&
      collateralFactor > 0 &&
      depositsUSDOracle > 0
    ) {
      // collateralRiskBps is stamped directly on the meta entry — the
      // entry IS one spoke's view of one asset, so this is the right
      // value by construction (no need to look it up via configs).
      const riskBps = Number((meta as any).collateralRiskBps ?? 0)
      collaterals.push({ riskBps, valueUsd: depositsUSDOracle })
    }

    if (debtUSDOracle > 0) {
      totalDebtForPremium += debtUSDOracle
    }
  }

  let riskPremiumBps = 0
  if (totalDebtForPremium > 0) {
    // On-chain: sortByKey sorts (collateralRisk ASC, collateralValue DESC) for
    // ties — KeyValueList encodes inverted key so descending sort on packed
    // words yields ascending key + descending value on ties.
    const sorted = collaterals
      .slice()
      .sort((a, b) => a.riskBps - b.riskBps || b.valueUsd - a.valueUsd)
    let debtLeft = totalDebtForPremium
    let weightedSum = 0
    for (const c of sorted) {
      if (debtLeft <= 0) break
      const used = Math.min(c.valueUsd, debtLeft)
      weightedSum += used * c.riskBps
      debtLeft -= used
    }
    const covered = totalDebtForPremium - debtLeft
    // contract rounds up (divup)
    riskPremiumBps = covered > 0 ? Math.ceil(weightedSum / covered) : 0
  }

  // ── Pass 1: Aggregate balances, collateral, and interest ──────────

  // organic yields
  let depositInterest = 0
  let borrowInterest = 0
  // rewards
  let rewardDepositAccrual = 0
  let rewardBorrowAccrual = 0
  // staking
  let stakingDepositAccrual = 0
  let stakingBorrowAccrual = 0
  // amounts
  let deposits = 0
  let debt = 0
  let oracleDebt = 0
  let collateral = 0
  let borrowDiscountedCollateral = 0
  let borrowDiscountedCollateralAllActive = 0
  let collateralAllActive = 0

  let rewardsPerAsset: any = {}

  for (const marketUid of positionMarketUids) {
    const meta = lenderData?.[marketUid]
    const pos = positions[marketUid]
    if (!meta || !pos) continue

    const { depositsUSD, debtStableUSD, debtUSD, collateralEnabled } = pos
    const depositsUSDOracle = (pos as any).depositsUSDOracle ?? depositsUSD
    const debtUSDOracle = (pos as any).debtUSDOracle ?? debtUSD

    const {
      depositRate,
      intrinsicYield,
      variableBorrowRate,
      stableBorrowRate,
      rewards,
      flags,
      configs,
    } = meta

    const userConfigForAsset = resolveDynamicConfig(
      configs as any,
      (pos as any).userConfigKey,
    )

    // `riskPremiumThreshold` (from Hub.SpokeData) is a global accrual gate:
    // if the spoke's aggregate premiumShares/drawnShares ratio breaches the
    // threshold, new draws are blocked at the Hub level. It does NOT cap the
    // individual user's displayed rate — getUserAccountData never applies it.
    const userPremiumMultiplier = 1 + riskPremiumBps / 10_000
    const effectiveVariableBorrowRate =
      (variableBorrowRate ?? 0) * userPremiumMultiplier
    // Expose the effective (user-specific) borrow rate on the position so
    // downstream consumers see the rate that actually applies to this user.
    ;(pos as any).variableBorrowRate = effectiveVariableBorrowRate
    ;(pos as any).riskPremiumBps = riskPremiumBps

    // amounts (display)
    deposits += depositsUSD
    debt += debtUSD + (debtStableUSD ?? 0)
    // oracle-based debt for risk
    oracleDebt +=
      debtUSDOracle + ((pos as any).debtStableUSDOracle ?? debtStableUSD ?? 0)

    // rewards
    ;(rewards ?? []).forEach((rewardData: any) => {
      const key = rewardData.asset
      rewardDepositAccrual += rewardData.depositRate * depositsUSD
      rewardBorrowAccrual += rewardData.variableBorrowRate * debtUSD

      const rewDepo = rewardData.depositRate * depositsUSD
      const rewDebt = rewardData.variableBorrowRate * debtUSD

      if (!rewardsPerAsset[key])
        rewardsPerAsset[key] = { depositApr: 0, borrowApr: 0 }
      if (rewDepo > 0) rewardsPerAsset[key].depositApr += rewDepo
      if (rewDebt > 0) rewardsPerAsset[key].borrowApr += rewDebt
    })

    // staking
    stakingDepositAccrual += (intrinsicYield ?? 0) * depositsUSD
    stakingBorrowAccrual += (intrinsicYield ?? 0) * debtUSD

    // collateral accounting (oracle-based for risk accuracy)
    if (
      flags?.collateralActive ||
      (userConfigForAsset && !userConfigForAsset.collateralDisabled)
    ) {
      if (collateralEnabled) {
        collateral +=
          (userConfigForAsset?.collateralFactor ?? 1) * depositsUSDOracle
        borrowDiscountedCollateral +=
          (userConfigForAsset?.borrowCollateralFactor ?? 1) * depositsUSDOracle
      }
      borrowDiscountedCollateralAllActive +=
        (userConfigForAsset?.borrowCollateralFactor ?? 1) * depositsUSDOracle
      collateralAllActive +=
        (userConfigForAsset?.collateralFactor ?? 1) * depositsUSDOracle
    }

    // interest rates — premium-adjusted on the borrow side
    depositInterest += (depositRate ?? 0) * depositsUSD
    borrowInterest +=
      debtUSD * effectiveVariableBorrowRate +
      (debtStableUSD ?? 0) * (stableBorrowRate ?? 0)
  }

  const nav = deposits - debt
  const balanceData = {
    borrowDiscountedCollateral,
    borrowDiscountedCollateralAllActive,
    collateral,
    collateralAllActive,
    deposits,
    debt,
    adjustedDebt: oracleDebt,
    nav,
    deposits24h: totalDeposits24h,
    debt24h: totalDebt24h,
    nav24h: totalDeposits24h - totalDebt24h,
    // Spoke-level risk premium in BPS — matches Spoke.getUserAccountData().riskPremium
    riskPremiumBps,
    ...(payload.rewards ? { rewards: payload.rewards } : {}),
  }

  const aprData = {
    apr: safeDivide(depositInterest - borrowInterest, nav),
    borrowApr: safeDivide(borrowInterest, debt),
    depositApr: safeDivide(depositInterest, deposits),
    rewards: divideAccrualsToAprs(rewardsPerAsset, nav, deposits, debt),
    rewardApr: safeDivide(rewardDepositAccrual + rewardBorrowAccrual, nav),
    rewardDepositApr: safeDivide(rewardDepositAccrual, deposits),
    rewardBorrowApr: safeDivide(rewardBorrowAccrual, debt),
    intrinsicApr: safeDivide(stakingDepositAccrual - stakingBorrowAccrual, nav),
    intrinsicDepositApr: safeDivide(stakingDepositAccrual, deposits),
    intrinsicBorrowApr: safeDivide(stakingBorrowAccrual, debt),
  }

  const userConfig = {
    selectedMode: '0',
    id: account,
    isWhitelisted: true,
  }

  const creditLine = Math.max(
    0,
    borrowDiscountedCollateral - balanceData.adjustedDebt,
  )

  // ── Pass 2: Compute withdrawable/borrowable per position ──────────
  // Iterates this spoke's marketUids only (filtered above). For assets
  // the user has touched, enriches the existing position with
  // withdrawable/borrowable. For assets the user hasn't touched but
  // could borrow against existing collateral, stubs in a borrow-only
  // entry. Other spokes' marketUids are not visible here.
  for (const marketUid of spokeMarketUids) {
    const meta = lenderData?.[marketUid]
    if (!meta) continue

    const { configs, flags, borrowLiquidity, withdrawLiquidity, depositable } =
      meta
    const pos = positions[marketUid]

    const config = resolveDynamicConfig(
      configs as any,
      pos ? (pos as any).userConfigKey : undefined,
    )
    const price = getSpokeScopedPrice(meta)
    const bcf = config?.borrowCollateralFactor ?? 1
    const bf = config?.borrowFactor ?? 1

    if (pos) {
      let withdrawable: string
      if (!pos.collateralEnabled || config?.collateralDisabled) {
        withdrawable = String(pos.deposits)
      } else if (balanceData.debt === 0) {
        withdrawable = String(pos.deposits)
      } else {
        const withdrawableUSD = Math.max(creditLine / bcf, 0)
        withdrawable = String(
          Math.min(withdrawableUSD / price, Number(pos.deposits)),
        )
      }
      if (withdrawLiquidity != null) {
        withdrawable = String(Math.min(Number(withdrawable), withdrawLiquidity))
      }

      let borrowable: string
      if (!flags?.borrowingEnabled || flags?.isFrozen || config?.debtDisabled) {
        borrowable = '0'
      } else {
        const borrowableUSD = Math.max(creditLine / bf, 0)
        borrowable = String(borrowableUSD / price)
      }
      if (borrowLiquidity != null) {
        borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
      }

      ;(pos as any).withdrawable = withdrawable
      ;(pos as any).borrowable = borrowable
      // depositable: global spoke headroom — same for all users in V4
      // (maxDeposit ignores the caller address on-chain).
      ;(pos as any).depositable =
        depositable == null ? null : String(depositable)
      ;(pos as any).underlyingInfo = buildUnderlyingInfo(meta)
    } else if (
      (deposits > 0 || debt > 0) &&
      flags?.borrowingEnabled &&
      !flags?.isFrozen &&
      !config?.debtDisabled
    ) {
      let borrowable = String(Math.max(creditLine / bf, 0) / price)
      if (borrowLiquidity != null) {
        borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
      }

      positions[marketUid] = {
        marketUid,
        deposits: '0',
        debt: '0',
        debtStable: '0',
        depositsUSD: 0,
        debtUSD: 0,
        debtStableUSD: 0,
        collateralEnabled: false,
        claimableRewards: 0,
        withdrawable: '0',
        borrowable,
        depositable: depositable == null ? null : String(depositable),
        underlyingInfo: buildUnderlyingInfo(meta),
      } as any
    }
  }

  return {
    lender: '',
    account,
    chainId,
    data: [
      {
        health:
          balanceData.debt === 0
            ? null
            : balanceData.adjustedDebt > 0
              ? balanceData.collateral / balanceData.adjustedDebt
              : balanceData.collateral / balanceData.debt,
        borrowCapacityUSD: creditLine,
        accountId: '0',
        balanceData,
        aprData,
        userConfig,
        positions: Object.values(positions) as any,
      },
    ],
  }
}
